import DrawnTrace from "../models/DrawnTrace";
import Point from "../models/Point";
import ReferenceStroke from "../models/ReferenceStroke";
import { getBBox, getPointAtLengthFraction } from "../models/SVGPath";

/**
 * **Feature Flag**: Generate separate scores for each stroke and ensure all of
 * them are under a threshold.
 *
 * Generate separate scores for each stroke and ensure all of them are under a
 * threshold. If this flag is off then a single score is created by
 * concatenating all strokes' feature vectors, as in the algorithm described in
 * fastdatascience/katakana/main.py.
 *
 * Though this might provide better results, but it's not a lot better. The
 * main problem we're having is that if the drawn strokes are drawn backwards
 * the validation still passes. For example, in the first stroke of ア. We
 * might need to give the start and end points more weight than the bounding
 * box, or verify them separately (or individually) since they're so important.
 */
const FEATURE_FLAG_SCORE_STROKES_SEPARATELY = false;

export default function verifyStrokes(
  referenceStrokes: ReferenceStroke[],
  drawnTraces: DrawnTrace[]
): { correct: boolean; message: string; score?: number } {
  if (
    referenceStrokes.length !== drawnTraces.length &&
    !(
      (referenceStrokes.length - 1 === drawnTraces.length ||
        referenceStrokes.length - 2 === drawnTraces.length) &&
      referenceStrokes.length > 2
    )
  ) {
    return {
      correct: false,
      message: `Strokes length required ${referenceStrokes.length} got ${drawnTraces.length}`,
    };
  }

  const referenceFeatures: Features[] = referenceStrokes.map((stroke) => ({
    start: getPointAtLengthFraction(stroke.trace.path, 0),
    end: getPointAtLengthFraction(stroke.trace.path, 1),
    bbox: getBBox(stroke.trace.path),
  }));
  const drawnFeatures: Features[] = drawnTraces.map((trace) => {
    const minX = Math.min(...trace.map(({ x }) => x));
    const minY = Math.min(...trace.map(({ y }) => y));
    return {
      start: trace[0],
      end: trace[trace.length - 1],
      bbox: new DOMRect(
        minX,
        minY,
        Math.max(...trace.map(({ x }) => x)) - minX,
        Math.max(...trace.map(({ y }) => y)) - minY
      ),
    };
  });

  const referenceFeatureVectors = referenceFeatures.map(getFeatureVector);
  const drawnFeatureVectors = drawnFeatures.map(getFeatureVector);
  const distanceVectors = drawnFeatureVectors.map((vector, vectorIndex) =>
    vector.map(
      (value, index) => value - referenceFeatureVectors[vectorIndex][index]
    )
  );

  console.debug({
    features: {
      referenceFeatures,
      drawnFeatures,
    },
    featureVectors: {
      referenceFeatureVectors,
      drawnFeatureVectors,
    },
    distanceVectors,
  });

  if (FEATURE_FLAG_SCORE_STROKES_SEPARATELY) {
    const scores = distanceVectors.map((distanceVector) =>
      Math.sqrt(
        distanceVector.reduce((sum, distance) => sum + distance ** 2, 0)
      )
    );
    return {
      correct: scores.every((score) => score < 40),
      message: `Minimum Strokes required ${
        referenceStrokes.length - 1
      } or equal`,
    };
  } else {
    const individualScore = distanceVectors.map((distanceVector) =>
      Math.sqrt(
        distanceVector.flat(1).reduce((sum, distance) => sum + distance ** 2, 0)
      )
    );
    const completeScore = Math.sqrt(
      distanceVectors.flat(1).reduce((sum, distance) => sum + distance ** 2, 0)
    );

    return {
      correct:
        referenceStrokes.length < 3 ? completeScore < 50 : completeScore < 25,
      message: (
        referenceStrokes.length < 3 ? completeScore < 50 : completeScore < 25
      )
        ? ""
        : `Stroke ${
            individualScore.indexOf(Math.max(...individualScore)) + 1
          } is Wrong`,
      score: completeScore,
    };
  }
}

interface Features {
  start: Point;
  end: Point;
  bbox: DOMRect;
}

function getFeatureVector({ start, end, bbox }: Features) {
  return [
    start.x,
    start.y,
    end.x,
    end.y,
    bbox.x,
    bbox.y,
    bbox.x + bbox.width,
    bbox.y + bbox.height,
  ];
}
